/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  createContext, useContext, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import confetti from 'canvas-confetti';

import api from '../../services/api';

import { useFlashErrorText } from '../../contexts/FlashErrorText';
import { useModal } from '../../contexts/Modal';
import { SelectionsContext } from '../../contexts/Selections';

import {
  IInitialFormState, IFormContext, IProps,
} from '../../interfaces/components/form.interface';
import { useAppLoading } from '../../contexts/AppLoading';
import { skipConfirmation } from './utils';
import ConfirmationModal from '../ConfirmationModal';

export const FormContext = createContext<IFormContext | undefined>(undefined);

const REDIRECT_DELAY_SECONDS = 3;

const Form = (props: IProps) => {
  const {
    autoSubmit,
    children,
    childrenComponents,
    className,
    id,
    postRoute,
    setComponentTree,
    confirmationModal,
    exitRedirect,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoginPageLoading, setAppLoading, appLoading } = useAppLoading();
  const { setFlashErrorText, setMetadataText } = useFlashErrorText();
  const {
    openModal: openModalWrapper,
  } = useModal();
  const selectionsContext = useContext(SelectionsContext);

  // we use the childrenComponents (part of the component tree from the backend) to initialize
  // state since we can receive initial values from the backend. also if validators are used
  // for inputs we can attach them to the state here
  const formInitialState: IInitialFormState = {};
  const getInitialFormState = (childComponents) => {
    for (let i = 0; i < childComponents.length; i += 1) {
      const component = childComponents[i];
      if (component.componentType === 'textInput' || component.componentType === 'select') {
        formInitialState[component.name] = {
          value: component.initialValue || '',
        };
        if (component.validator) {
          formInitialState[component.name].validator = component.validator;
        }
      } else if (component.componentType === 'toggleInput' || component.componentType === 'vehicleToggle') {
        if (component.inputType === 'radio') {
          // select the first radio button available
          formInitialState.selectedRadio = formInitialState.selectedRadio
            ? formInitialState.selectedRadio
            : component.value;
        } else if (component.inputType === 'checkbox') {
          if (!formInitialState[component.name]) {
            formInitialState[component.name] = {};
          }
          if (component.value) {
            formInitialState[component.name][component.value] = Boolean(component.defaultChecked);
          }
        }
      } else if (component.componentType === 'buttonOption') {
        formInitialState.selectedButtonOption = {
          value: '',
          validator: (value) => {
            if (!value) {
              setFlashErrorText(component.errorMessage);
              return false;
            }
            return true;
          },
        };
      } else if (component.componentType === 'hidden') {
        let { value } = component;
        if (component.name === 'sdkHasBle') {
          value = Boolean((window as any).SmartcarSDKBLE);
        // used to populate the make field for disable make email submission, since it
        // is user selected on the FE
        } else if (component.name === 'make' && !value) {
          value = selectionsContext?.makeObject?.make;
        }
        formInitialState[component.name] = value;
      } else if (component.componentType === 'reCAPTCHA') {
        const reCAPTCHARef = useRef();
        formInitialState.reCAPTCHARef = reCAPTCHARef;
        formInitialState.make = component.make;
      } else if (component.componentType === 'oemCaptcha') {
        formInitialState.oemCaptchaType = component.oemCaptchaType;
        formInitialState.oemCaptchaSiteKey = component.oemCaptchaSiteKey;
        formInitialState.oemCaptchaToken = null;
      } else if (component.componentType === 'virtualKeyVehicles') {
        formInitialState.vehicles = component.vehicles.reduce(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          (acc, vehicle) => ({
            ...acc,
            [vehicle.id]: {
              value: Boolean(vehicle.virtualKeyRequirement && ['paired', 'notRequired'].includes(vehicle.virtualKeyRequirement)),
            },
          }),
          {},
        );
      } else if (component.componentType === 'bluetoothVehicles') {
        formInitialState.pairedVehicles = component.vehicles.reduce(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          (acc, vehicle) => ({
            ...acc,
            [vehicle.id]: {
              value: !vehicle.requiresPairing,
            },
          }),
          {},
        );
      } else if (component.childrenComponents) {
        getInitialFormState(component.childrenComponents);
      }
    }
  };
  getInitialFormState(childrenComponents);

  const [formState, setFormState] = useState(formInitialState);
  const [formValidated, setFormValidated] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const submitFormData = async () => {
    // reset any flash errors
    setFlashErrorText('');

    try {
      const formData = {
        ...formState,
      };
      if (formData.reCAPTCHARef) {
        const token = await formData.reCAPTCHARef.current?.executeAsync();

        // reset the reCAPTCHA widget so that if the user attempts to submit the form in again,
        // it is available for use (see react-google-recaptcha issues #191, 198, 201)
        await formData.reCAPTCHARef.current?.reset();

        // replace reCAPTCHARef property with recaptchaToken for consumption on backend
        delete formData.reCAPTCHARef;
        formData.recaptchaToken = token;
      }

      const { data } = await api.postBackend(postRoute, formData);

      setFormValidated(false);
      setAppLoading(false);
      setLoginPageLoading(false);

      // need to use window.location.href here bc we are redirecting outside of Connect
      if (data.redirect) {
        window.location.href = data.redirect;
      } else if (data.routes) {
        navigate(data.routes.frontend);
      } else if (data.modal) {
        // handle case where modal needs to close after form submission
        // if (data.modal === 'close') {
        //   closeModalWrapper();
        //   return;
        // }
        if (data.error) {
          setFlashErrorText(data.error);
        }
        openModalWrapper(data.modal);
      } else {
        setComponentTree(data);
      }
    } catch (err: any) {
      // clear forms
      setFormState(formInitialState);
      setFormValidated(false);
      setLoginPageLoading(false);
      setAppLoading(false);

      if (err.response && err.response.data) {
      // display flash error
        if (err.response.data.flash) {
          setFlashErrorText(err.response.data.message);
          console.log(`Session ID: ${err.response.data.sessionId as string}`);

          if (err.response.data.metadata) {
            setMetadataText(err.response.data.metadata);
          }
        } else {
        // display page error
          setFlashErrorText('');
          setComponentTree(err.response.data);
        }
      }
    }
  };

  const handleExitRedirect = (() => {
    let hasExecuted = false;
    return () => {
      // ensures that the exit redirect only executes once
      if (!hasExecuted && exitRedirect) {
        hasExecuted = true;
        window.location.href = exitRedirect;
      }
    };
  })();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (confirmationModal && !skipConfirmation(formState, confirmationModal.skipValidator)
    ) {
      if (!modalOpen) {
        openModal();
        return;
      }
      closeModal();
    }

    if (exitRedirect) {
      if (appLoading) {
        return;
      }
      setAppLoading(true);
      handleExitRedirect();
      return;
    }

    // validate form data if validators present
    // currently there are no validators implemented
    const allValidated = Object.keys(formState).every((name) => {
      const item = formState[name];
      if (item.validator) {
        return item.validator(item.value);
      }
      return true;
    });

    if (!allValidated) {
      setFormValidated(false);

      return;
    }

    // This logic is for rendering the loading animation page specifically for the Login Page after
    // a user enters their credentials.
    if ((location.pathname === '/login' || '/return/login') && ('password' in formState)) {
      setLoginPageLoading(true);
    } else {
      setAppLoading(true);
    }

    setFormValidated(true);
    await submitFormData();
  };

  const contextValue: IFormContext = useMemo(() => ({
    formState,
    setFormState,
    submitFormData: autoSubmit ? submitFormData : null,
    formValidated,
  }), [formState, formValidated]);

  const actionMap = {
    confirm: handleSubmit,
    cancel: closeModal,
  };

  if (exitRedirect) {
    confetti({
      particleCount: 30,
      spread: 50,
    });
    setTimeout(() => {
      if (!appLoading) {
        setAppLoading(true);
        handleExitRedirect();
      }
    }, REDIRECT_DELAY_SECONDS * 1000);
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form className={clsx(className)} id={id} onSubmit={handleSubmit}>
      <FormContext.Provider value={contextValue}>
        <>
          {children}
          {confirmationModal
            && (
            <ConfirmationModal
              open={modalOpen}
              closeModal={closeModal}
              actionMap={actionMap}
              {...confirmationModal}
            />
            )}
        </>
      </FormContext.Provider>
    </form>
  );
};

export default Form;
